import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "../axiosSetup";
import CustomNavbar from "../CustomNavbar";
import cfg from "../../config";

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
});

const PHASE_LABEL  = { bus: "Hinfahrt", assembly: "Aufstellungsplatz" };
const RESULT_CFG   = {
  confirmed:         { label: "Bestätigt",       bg: "success" },
  already_confirmed: { label: "Doppel-Scan",     bg: "warning" },
};

const fmtDateTime = (iso) => {
  if (!iso) return "–";
  return new Date(iso).toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
};

const AdminQRScanLog = () => {
  const [rows, setRows]       = useState([]);
  const [moves, setMoves]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  // Filter-State
  const [search,    setSearch]    = useState("");
  const [moveFilter, setMoveFilter] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("");
  const [resultFilter, setResultFilter] = useState("");
  const [dateFrom,  setDateFrom]  = useState("");
  const [dateTo,    setDateTo]    = useState("");
  const [limit,     setLimit]     = useState(500);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef(null);

  // Umzüge laden (einmalig)
  useEffect(() => {
    api.get("/api/moves-simple", authHeader())
      .then((r) => setMoves(r.data))
      .catch(() => {});
  }, []);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit });
      if (moveFilter)   params.set("moveID",   moveFilter);
      if (phaseFilter)  params.set("phase",    phaseFilter);
      if (resultFilter) params.set("result",   resultFilter);
      if (dateFrom)     params.set("dateFrom", dateFrom);
      if (dateTo)       params.set("dateTo",   dateTo);
      if (search.trim()) params.set("search",  search.trim());
      const res = await api.get(`/api/admin/qr-scan-log?${params}`, authHeader());
      setRows(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "Fehler beim Laden des Logs");
    } finally {
      setLoading(false);
    }
  }, [moveFilter, phaseFilter, resultFilter, dateFrom, dateTo, search, limit]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchLog, 15_000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, fetchLog]);

  const total      = rows.length;
  const confirmed  = rows.filter((r) => r.result === "confirmed").length;
  const duplicates = rows.filter((r) => r.result === "already_confirmed").length;

  const exportCSV = () => {
    const header = ["Zeitstempel", cfg.memberTerm, `${cfg.memberTerm}-ID`, `${cfg.costumeTerm}-Typ`, `${cfg.costumeTerm}-Nr.`, cfg.eventTerm, "Datum", "Phase", "Ergebnis", "Gescannt von"];
    const csvRows = rows.map((r) => [
      fmtDateTime(r.scannedAt),
      r.memberName,
      r.memberID,
      r.haesTyp,
      r.haesNumber,
      r.moveCity,
      r.moveDate,
      PHASE_LABEL[r.phase] ?? r.phase,
      RESULT_CFG[r.result]?.label ?? r.result,
      r.scannedByName,
    ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";"));
    const blob = new Blob(["﻿" + [header.join(";"), ...csvRows].join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-scan-log-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <CustomNavbar />
      <div className="container-fluid mt-4 px-3 px-md-4">

        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div>
            <h3 className="mb-0">QR-Code Audit-Log <span className="badge bg-danger ms-1 small">Admin</span></h3>
            <small className="text-muted">Vollständige Scan-Historie — durchsuchbar und filterbar</small>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="autoRefresh"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="autoRefresh">Live (15 s)</label>
            </div>
            <button className="btn btn-outline-secondary btn-sm" onClick={fetchLog} disabled={loading}>
              {loading
                ? <span className="spinner-border spinner-border-sm" />
                : <i className="bi bi-arrow-clockwise" />
              }{" "}Aktualisieren
            </button>
            <button className="btn btn-outline-success btn-sm" onClick={exportCSV} disabled={rows.length === 0}>
              <i className="bi bi-download" /> CSV
            </button>
          </div>
        </div>

        {/* ── Statistik-Karten ────────────────────────────────────── */}
        <div className="row g-2 mb-3">
          {[
            { label: "Scans gesamt",  value: total,      color: "primary" },
            { label: "Erfolgreich",   value: confirmed,  color: "success" },
            { label: "Doppel-Scans",  value: duplicates, color: "warning" },
          ].map(({ label, value, color }) => (
            <div key={label} className="col-4 col-md-2">
              <div className={`card border-${color} text-center py-2`}>
                <div className={`fs-4 fw-bold text-${color}`}>{value}</div>
                <div className="small text-muted">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Filter ──────────────────────────────────────────────── */}
        <div className="card mb-3">
          <div className="card-body py-2">
            <div className="row g-2">

              {/* Namenssuche */}
              <div className="col-12 col-md-4">
                <input
                  type="search"
                  className="form-control form-control-sm"
                  placeholder="Name suchen…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Umzug */}
              <div className="col-12 col-sm-6 col-md-3">
                <select className="form-select form-select-sm" value={moveFilter} onChange={(e) => setMoveFilter(e.target.value)}>
                  <option value="">Alle {cfg.eventTermPlural}</option>
                  {moves.map((m) => (
                    <option key={m.ID} value={m.ID}>{m.city} – {m.date}</option>
                  ))}
                </select>
              </div>

              {/* Phase */}
              <div className="col-6 col-sm-3 col-md-2">
                <select className="form-select form-select-sm" value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)}>
                  <option value="">Alle Phasen</option>
                  <option value="bus">Hinfahrt</option>
                  <option value="assembly">Aufstellungsplatz</option>
                </select>
              </div>

              {/* Ergebnis */}
              <div className="col-6 col-sm-3 col-md-2">
                <select className="form-select form-select-sm" value={resultFilter} onChange={(e) => setResultFilter(e.target.value)}>
                  <option value="">Alle Ergebnisse</option>
                  <option value="confirmed">Bestätigt</option>
                  <option value="already_confirmed">Doppel-Scan</option>
                </select>
              </div>

              {/* Datum von */}
              <div className="col-6 col-sm-4 col-md-2">
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  title="Datum von"
                />
              </div>

              {/* Datum bis */}
              <div className="col-6 col-sm-4 col-md-2">
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  title="Datum bis"
                />
              </div>

              {/* Limit */}
              <div className="col-6 col-sm-4 col-md-2">
                <select className="form-select form-select-sm" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                  {[100, 250, 500, 1000, 2000, 5000].map((n) => (
                    <option key={n} value={n}>Max. {n}</option>
                  ))}
                </select>
              </div>

              {/* Reset */}
              <div className="col-6 col-sm-4 col-md-2">
                <button
                  className="btn btn-outline-secondary btn-sm w-100"
                  onClick={() => { setSearch(""); setMoveFilter(""); setPhaseFilter(""); setResultFilter(""); setDateFrom(""); setDateTo(""); setLimit(500); }}
                >
                  Filter zurücksetzen
                </button>
              </div>

            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {rows.length === 0 && !loading ? (
          <div className="alert alert-info">Keine Einträge für die gewählten Filter.</div>
        ) : (
          <>
            {/* ── Tabelle (ab md) ────────────────────────────────── */}
            <div className="d-none d-md-block">
              <div className="table-responsive">
                <table className="table table-hover table-sm align-middle">
                  <thead className="table-dark">
                    <tr>
                      <th>Zeitstempel</th>
                      <th>{cfg.memberTerm}</th>
                      <th>{cfg.costumeTerm}</th>
                      <th>{cfg.eventTerm}</th>
                      <th>Phase</th>
                      <th>Ergebnis</th>
                      <th>Gescannt von</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const res = RESULT_CFG[r.result] ?? { label: r.result, bg: "secondary" };
                      return (
                        <tr key={r.id}>
                          <td className="text-nowrap small">{fmtDateTime(r.scannedAt)}</td>
                          <td>
                            <span className="fw-semibold">{r.memberName}</span>
                            <span className="text-muted small ms-1">#{r.memberID}</span>
                          </td>
                          <td className="text-nowrap">
                            {r.haesTyp} {r.haesNumber}
                            {r.isKidsHaes ? <span className="badge bg-info ms-1 small">Kind</span> : null}
                          </td>
                          <td className="text-nowrap">
                            {r.moveCity}<br />
                            <small className="text-muted">{r.moveDate}</small>
                          </td>
                          <td>{PHASE_LABEL[r.phase] ?? r.phase}</td>
                          <td>
                            <span className={`badge bg-${res.bg} text-${res.bg === "warning" ? "dark" : "white"}`}>
                              {res.label}
                            </span>
                          </td>
                          <td>{r.scannedByName}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Cards (Handy) ───────────────────────────────────── */}
            <div className="d-md-none d-flex flex-column gap-2">
              {rows.map((r) => {
                const res = RESULT_CFG[r.result] ?? { label: r.result, bg: "secondary" };
                return (
                  <div key={r.id} className="card shadow-sm">
                    <div className="card-body py-2 px-3">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <span className="small text-muted">{fmtDateTime(r.scannedAt)}</span>
                        <span className={`badge bg-${res.bg} text-${res.bg === "warning" ? "dark" : "white"}`}>
                          {res.label}
                        </span>
                      </div>
                      <div className="fw-semibold">
                        {r.memberName}
                        <span className="text-muted small ms-1">#{r.memberID}</span>
                      </div>
                      <div className="small">
                        {r.haesTyp} {r.haesNumber}
                        {r.isKidsHaes ? <span className="badge bg-info ms-1">Kind</span> : null}
                        {" · "}{r.moveCity} {r.moveDate}
                      </div>
                      <div className="small text-muted mt-1">
                        Phase: <strong>{PHASE_LABEL[r.phase] ?? r.phase}</strong>
                        {" · "}von: <strong>{r.scannedByName}</strong>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminQRScanLog;
